import DeliveryTypeFormComponent from "Components/order-management/delivery-type-form/delivery-type-form";
import InsuranceRateFormComponent from "Components/order-management/insurance-rate-form/insurance-rate-form";
import IssueTypeFormComponent from "Components/order-management/issue-type-form/issue-type-form";
import { LocationFormComponent } from "Components/order-management/location-form";
import { RateTableFormComponent } from "Components/order-management/rate-table-form";
import ServiceTypeFormComponent from "Components/order-management/service-type-form/service-type-form";
import SpecialConditionCareFormComponent from "Components/order-management/special-condition-care-form/special-condition-care-form";
import TypeOfGoodsFormComponent from "Components/order-management/type-of-goods-form/type-of-goods-form";
import { OrderSettingsEnum } from "Data/settings";
import { getEnumOfOrderSettings } from "Helpers/order-settings-helper";
import { toaster } from "Helpers/toaster";
import useAccessControlList from "Hooks/access-control.hook";
import useSession from "Hooks/session.hook";
import React, { useMemo, useState } from "react";
import { useEffect } from "react";
import {
  createOrderSetting,
  fetchOrderSetting,
  fetchOrderSettingList,
  updateOrderSetting,
} from "Services/order-setting.service";
import { isNil } from "lodash";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { orderSettingAtom } from "Recoil/order_setting.atom";
import { decode } from "Helpers/enc";
import { fetchCustomerList } from "Services/customer.service";
import {
  createCustomerRateTableSetting,
  fetchCustomerRateTable,
  fetchUnassignedCustomerList,
  updateCustomerRateTableSetting,
} from "Services/customer-rate-table.services";

// special class name
const expandedWidth = [
  OrderSettingsEnum.RATE_TABLE,
  OrderSettingsEnum.CUSTOMER_RATE_TABLE,
];

interface useOrderSettingsEditProps {
  isEdit?: boolean;
}

const useOrderSettingsEdit = (props?: useOrderSettingsEditProps) => {
  const { isEdit = false } = props || {};

  const { type, id } = useParams<{ type: string; id: string }>();
  const { roleTypes, org } = useSession();
  const history = useHistory();

  const [activeComponent, setActiveComponent] = useState(<></>);
  const [orderSettingState, setOrderSettingState] =
    useRecoilState<any>(orderSettingAtom);
  const [orderSettingOptions, setOrderSettingOptions] = useState<any>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Rider_Management",
  });

  const handleUpdateOrderSettings = async (data) => {
    data.type = getEnumOfOrderSettings(type);

    let responseData: any = {};

    if (data.type === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      const payload = {
        ...data,
        orgID: org?._id,
      };

      responseData = await updateCustomerRateTableSetting(payload);
    } else {
      responseData = await updateOrderSetting(data);
    }

    if (!isNil(responseData) && responseData.success) {
      toaster("success", "Updated Successfully!");
      history.push("/order-management/settings?tabKey=" + type);
    } else {
      toaster("error", "Update Failed");
    }
  };

  const handleFetchOrderSettingsList = async (filters) => {
    const responseData = await fetchOrderSettingList(filters);
    setOrderSettingOptions({
      ...orderSettingOptions,
      [filters.type]: responseData,
    });
  };

  const handleFetchCustomerList = async (listAll: boolean = false) => {
    setIsLoading && setIsLoading(true);

    const responseData = await fetchUnassignedCustomerList({
      orgID: org?._id,
      listAll: listAll,
    });

    if (!responseData.error) {
      const customers = responseData.map((r) => ({
        label: r?.name,
        value: r?._id,
      }));
      setOrderSettingOptions((prev) => ({ ...prev, customersList: customers }));
    } else {
      console.log("Error: failed to fetch data");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchOrderSettings = async () => {
    setOrderSettingState({
      ...orderSettingState,
      status: { ...orderSettingState.status, fetch: "loading" },
    });

    let responseData: any = {};

    const decodedID = decode(id);
    const typeOfForm = getEnumOfOrderSettings(type);

    if (typeOfForm === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      responseData = await fetchCustomerRateTable(decodedID);
    } else {
      responseData = await fetchOrderSetting(decodedID);
    }

    if (!isNil(responseData)) {
      setOrderSettingState({
        ...orderSettingState,
        data: responseData,
        status: { ...orderSettingState.status, fetch: "success" },
      });
    } else {
      setOrderSettingState({
        ...orderSettingState,
        status: { ...orderSettingState.status, fetch: "error" },
      });
    }
  };

  const handleCreateOrderSettings = async (data) => {
    data.type = getEnumOfOrderSettings(type);

    let responseData: any = {};

    if (data.type === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      const payload = {
        ...data,
        orgID: org?._id,
      };

      responseData = await createCustomerRateTableSetting(payload);
    } else {
      responseData = await createOrderSetting(data);
    }

    if (!isNil(responseData) && responseData.success) {
      if (!responseData.data.created) {
        toaster("error", "Location Already in Use");
      } else {
        toaster("success", "Created Successfully!");
        history.push(`/order-management/settings?tabKey=${type}&refresh=true`);
      }
    } else {
      const { message } = responseData?.error?.message;

      if (message) {
        toaster("error", `${message}`);
      } else {
        toaster(
          "error",
          `${responseData?.error?.message}. ${responseData?.error?.info}`,
        );
      }
    }
  };

  const editInfo = useMemo(() => {
    return isEdit ? orderSettingState.data : null;
  }, [isEdit, orderSettingState.data]);

  useEffect(() => {
    if (type) {
      let form;

      const nameOfType = getEnumOfOrderSettings(type);

      switch (nameOfType) {
        case OrderSettingsEnum.SPECIAL_CARE:
        case OrderSettingsEnum.SPECIAL_CONDITION:
          form = (
            <SpecialConditionCareFormComponent
              info={editInfo}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        case OrderSettingsEnum.SERVICE_TYPE:
          form = (
            <ServiceTypeFormComponent
              info={editInfo}
              onFetch={handleFetchOrderSettingsList}
              settingOptions={orderSettingOptions}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        case OrderSettingsEnum.DELIVERY_TYPE:
          form = (
            <DeliveryTypeFormComponent
              info={editInfo}
              onFetch={handleFetchOrderSettingsList}
              settingOptions={orderSettingOptions}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        case OrderSettingsEnum.LOCATION:
          form = (
            <LocationFormComponent
              info={editInfo}
              onFetch={handleFetchOrderSettingsList}
              settingOptions={orderSettingOptions}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        case OrderSettingsEnum.RATE_TABLE:
        case OrderSettingsEnum.CUSTOMER_RATE_TABLE:
          form = (
            <RateTableFormComponent
              settingType={nameOfType}
              info={editInfo}
              onFetch={handleFetchOrderSettingsList}
              onFetchCustomerList={handleFetchCustomerList}
              settingOptions={orderSettingOptions}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
              isCustomer={nameOfType === OrderSettingsEnum.CUSTOMER_RATE_TABLE}
            />
          );
          break;
        case OrderSettingsEnum.ISSUE_TYPE:
          form = (
            <IssueTypeFormComponent
              info={editInfo}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        case OrderSettingsEnum.TYPE_OF_GOODS:
          form = (
            <TypeOfGoodsFormComponent
              info={editInfo}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        case OrderSettingsEnum.INSURANCE:
          form = (
            <InsuranceRateFormComponent
              info={editInfo}
              onSubmit={handleCreateOrderSettings}
              onUpdate={handleUpdateOrderSettings}
            />
          );
          break;
        default:
      }

      setActiveComponent(form);
    }
  }, [type, orderSettingState.data, orderSettingOptions]);

  return {
    expandedWidth,
    type,
    id,
    activeComponent,
    setActiveComponent,
    orderSettingState,
    setOrderSettingState,
    orderSettingOptions,
    setOrderSettingOptions,
    isLoading,
    setIsLoading,
    permission,
    handleFetchOrderSettingsList,
    handleFetchOrderSettings,
    handleCreateOrderSettings,
  };
};

export default useOrderSettingsEdit;
